import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import moment from 'moment';

import { Default } from '../../../../layouts';
import useDatasource from '../../../../hooks/useDatasource';
import {
  deleteHoliday,
  getHolidays,
} from '../../../../services/taxonomies/holidays';
import PageTitle from '../../../../components/PageTitle';
import AddEntityButton from '../../../../components/buttons/AddEntityButton';
import EditItemIcon from '../../../../components/buttons/EditItemIcon';
import DeleteItemIcon from '../../../../components/buttons/DeleteItemIcon';
import Column from '../../../../helpers/columns';
import usePermissions from '../../../../hooks/usePermission';
import ViewItemIcon from '../../../../components/buttons/ViewItemIcon';

const permissionsMap = {
  canView: ['ADMINISTRATION_TAXONOMIES_VIEW_PERMISSION'],
  canEdit: ['SYS_SUPERUSER', 'TAXONOMIES_PERMISSION'],
  canDelete: ['SYS_SUPERUSER', 'TAXONOMIES_PERMISSION'],
};

const HolidaysList = () => {
  const { t } = useTranslation();
  const { canView, canEdit, canDelete } = usePermissions(permissionsMap);
  const { pagination, content, handleChange, reload } =
    useDatasource(getHolidays);

  const columns = useMemo(
    () => [
      Column.text('name', t('entity.taxonomy.holiday.name'), { filter: true }),
      {
        title: t('entity.taxonomy.holiday.holidayDate'),
        dataIndex: 'holidayDate',
        key: 'holidayDate',
        sorter: true,
        width: 150,
        render: (v) => moment(v, 'YYYYMMDD').format('YYYY-MM-DD'),
      },
      Column.bool('deleted', t('entity.taxonomy.holiday.deleted._'), {
        width: 150,
        filter: true,
        inverted: true,
        labels: [
          t('table.filter.all'),
          t('entity.taxonomy.holiday.deleted.true'),
          t('entity.taxonomy.holiday.deleted.false'),
          t('table.filter.unknown'),
        ],
      }),
      Column.actions(t('table.actions'), (record) => (
        <>
          {canView && (
            <ViewItemIcon path={`/admin/taxonomies/holidays/${record.id}`} />
          )}
          {canEdit && (
            <EditItemIcon path={`/admin/taxonomies/holidays/${record.id}`} />
          )}
          {canDelete && (
            <DeleteItemIcon
              title={t('entity.taxonomy.holiday._delete', record)}
              message={t('entity.taxonomy.holiday._deleted', record)}
              item={record}
              action={deleteHoliday}
              reload={reload}
            />
          )}
        </>
      )),
    ],
    [t, canView, canEdit, canDelete, reload],
  );

  return (
    <Default>
      <PageTitle title={t('entity.taxonomy.holiday._plural')}>
        {canEdit && (
          <AddEntityButton
            entity={t('entity.taxonomy.holiday._singular')}
            path="/admin/taxonomies/holidays/new"
          />
        )}
      </PageTitle>
      <Table
        columns={columns}
        rowKey="id"
        // loading={loading}
        pagination={pagination}
        dataSource={content}
        onChange={handleChange}
        scroll={{ x: 700, y: 700 }}
      />
    </Default>
  );
};

export default HolidaysList;
